import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  TextField as MuiTextField,
} from '@mui/material';
import { Form, Field } from 'react-final-form';
import { toast } from 'react-toastify';

import { UiInput, UiDatePicker } from 'src/ui/ui-kit';
import { getToken } from 'src/auth';
import { api } from 'src/api/config';

/**
 * Commission Payout Modal
 * Displays payout history and form to mark new payouts
 */
const CommissionPayoutModal = ({ open, onClose, fiId, fiName, onPayoutCreated, token }) => {
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch payout history when modal opens
  useEffect(() => {
    if (open && fiId && token && token.accessToken) {
      fetchPayouts();
    }
  }, [open, fiId, token]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token || !token.accessToken) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${api.hostname}/api/v1/commission-payouts/?terra_fi_id=${fiId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch payouts');
      }
      const data = await response.json();
      setPayouts(data || []);
    } catch (err) {
      console.error('Failed to load payout history:', err);
      setError(err.message || 'Failed to load payout history');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPayout = async (values) => {
    try {
      setSubmitting(true);

      if (!token || !token.accessToken) {
        throw new Error('Authentication token not available');
      }

      const payload = {
        terra_fi_id: fiId,
        amount_paid: parseFloat(values.amount_paid),
        payout_date: values.payout_date,
        period_start: values.period_start,
        period_end: values.period_end,
        status: 'completed',
        notes: values.notes || '',
      };

      const response = await fetch(`${api.hostname}/api/v1/commission-payouts/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
      }

      toast.success('Commission payout recorded successfully');

      // Refresh payout list
      await fetchPayouts();

      // Notify parent component
      if (onPayoutCreated) {
        onPayoutCreated();
      }

      // Reset form (will happen automatically if we return undefined)
      return undefined;
    } catch (err) {
      console.error('Failed to create payout:', err);
      const errorMsg = err.error || 'Failed to create payout';
      toast.error(errorMsg);

      // Return errors object for form to display
      return {
        amount_paid: err.amount_paid?.[0],
        payout_date: err.payout_date?.[0],
        period_start: err.period_start?.[0],
        period_end: err.period_end?.[0],
      };
    } finally {
      setSubmitting(false);
    }
  };

  const validatePayoutForm = (values) => {
    const errors = {};

    if (!values.amount_paid || parseFloat(values.amount_paid) <= 0) {
      errors.amount_paid = 'Amount must be greater than zero';
    }

    if (!values.payout_date) {
      errors.payout_date = 'Required';
    }

    if (!values.period_start) {
      errors.period_start = 'Required';
    }

    if (!values.period_end) {
      errors.period_end = 'Required';
    }

    if (values.period_start && values.period_end && values.period_start >= values.period_end) {
      errors.period_end = 'Period end must be after period start';
    }

    if (values.payout_date && values.period_end && values.payout_date < values.period_end) {
      errors.payout_date = 'Payout date cannot be before period end';
    }

    return errors;
  };

  const formatCurrency = (value) => {
    if (value == null) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (status) => {
    const statusColors = {
      completed: 'success',
      pending: 'warning',
      cancelled: 'default',
    };

    return (
      <Chip
        label={status?.toUpperCase()}
        color={statusColors[status] || 'default'}
        size="small"
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Commission Management - {fiName}
      </DialogTitle>
      <DialogContent>
        {/* Payout History Table */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Payout History
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : payouts.length === 0 ? (
            <Alert severity="info">No payout records found for this FI.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Payout Date</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Amount Paid</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payouts.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>{formatDate(payout.payout_date)}</TableCell>
                      <TableCell>
                        {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(payout.amount_paid)}</TableCell>
                      <TableCell align="center">{getStatusChip(payout.status)}</TableCell>
                      <TableCell>{payout.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Mark as Paid Form */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Record New Payout
          </Typography>

          <Form
            onSubmit={handleSubmitPayout}
            validate={validatePayoutForm}
            render={({ handleSubmit, pristine, submitting: formSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Field
                    name="amount_paid"
                    render={({ input, meta }) => (
                      <MuiTextField
                        {...input}
                        label="Amount Paid"
                        type="number"
                        required
                        fullWidth
                        inputProps={{ step: '0.01', min: '0.01' }}
                        error={meta.touched && meta.error}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  />

                  <Field
                    name="payout_date"
                    component={UiDatePicker}
                    label="Payout Date"
                    required
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Field
                      name="period_start"
                      component={UiDatePicker}
                      label="Period Start Date"
                      required
                    />

                    <Field
                      name="period_end"
                      component={UiDatePicker}
                      label="Period End Date"
                      required
                    />
                  </Box>

                  <Field
                    name="notes"
                    render={({ input, meta }) => (
                      <MuiTextField
                        {...input}
                        label="Notes"
                        multiline
                        rows={3}
                        fullWidth
                        error={meta.touched && meta.error}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  />

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={onClose} disabled={submitting || formSubmitting}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={pristine || submitting || formSubmitting}
                    >
                      {submitting || formSubmitting ? 'Submitting...' : 'Record Payout'}
                    </Button>
                  </Box>
                </Box>
              </form>
            )}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  token: getToken(state),
});

export default connect(mapStateToProps)(CommissionPayoutModal);
